import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { puctures } from '../../assets/images';

interface NavItem {
  title: string;
  path?: string;
  hasDropdown?: boolean;
  dropdownItems?: {
    title: string;
    path: string;
    description: string;
    image: string;
  }[];
}

const Navbar: React.FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems: NavItem[] = [
    {
      title: 'Transitional HR Services',
      path: '/transitional-hr'
    },
    {
      title: 'Strategic HR',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Onboarding & Offboarding Strategy & Management',
          path: '/strategic-hr/onboarding-offboarding',
          description: 'Streamline employee transitions for better experiences and outcomes',
          image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75'
        },
        {
          title: 'Benefits Strategy & Design',
          path: '/strategic-hr/benefits-strategy',
          description: 'Create competitive benefits packages that attract and retain top talent',
          image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75'
        },
        {
          title: 'HR Systems Design, Research & Implementation',
          path: '/strategic-hr/hr-systems',
          description: 'Implement cutting-edge HR technology solutions for your organization',
          image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75'
        },
        {
          title: 'Company Policies & Procedures Design',
          path: '/strategic-hr/policies-procedures',
          description: 'Develop comprehensive policies that protect and guide your organization',
          image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75'
        },
        {
          title: 'Performance Management & Design',
          path: '/strategic-hr/performance-management',
          description: 'Design effective performance systems that align with your business goals',
          image: puctures.strategicHR
        },
        {
          title: 'Employee Engagement & Culture',
          path: '/strategic-hr/employee-engagement',
          description: 'Build a thriving workplace culture that drives engagement and retention',
          image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75'
        },
      ]
    },
    {
      title: 'Services & Pricing',
      path: '/pricing'
    },
    {
      title: 'Who We Are',
      path: '/who-we-are'
    }
  ];

  const handleDropdownToggle = (itemTitle: string) => {
    if (activeDropdown === itemTitle) {
      setIsDropdownOpen(false);
      setActiveDropdown(null);
    } else {
      setActiveDropdown(itemTitle);
      setIsDropdownOpen(true);
    }
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="bg-white shadow-lg fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src={puctures.logo} 
                alt="ERHR Global" 
                className="h-12 w-auto"
                width={120}
                height={48}
                loading="eager"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <div key={item.title} className="relative">
                  {item.hasDropdown ? (
                    <button
                      onClick={() => handleDropdownToggle(item.title)}
                      className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center"
                    >
                      {item.title}
                      <svg
                        className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                          activeDropdown === item.title ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  ) : (
                    <Link
                      to={item.path || '/'}
                      className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors duration-200"
                    >
                      {item.title}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* LinkedIn Icon */}
              <a
                href="https://www.linkedin.com/company/er-hr-global/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-primary-600 transition-colors duration-200 p-2"
                aria-label="Visit our LinkedIn page"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              
              {/* Contact Button */}
              <Link
                to="/contact"
                className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
              >
                Contact Us
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="text-gray-700 hover:text-primary-600 p-2"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Full-screen Dropdown Menu */}
      <AnimatePresence>
        {isDropdownOpen && activeDropdown && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-white z-40 pt-16 overflow-y-auto"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-full">
              <div className="flex justify-between items-start mb-8">
                <h2 className="text-3xl font-bold text-gray-900">{activeDropdown}</h2>
                <button
                  onClick={closeDropdown}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {navItems
                  .find(item => item.title === activeDropdown)
                  ?.dropdownItems?.map((dropdownItem, index) => (
                    <motion.div
                      key={dropdownItem.title}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 + index * 0.1, duration: 0.4 }}
                    >
                      <Link
                        to={dropdownItem.path}
                        onClick={closeDropdown}
                        className="block bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group"
                      >
                        <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-primary-100 to-primary-200">
                          <img
                            src={dropdownItem.image}
                            alt={dropdownItem.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            width={400}
                            height={192}
                            loading="lazy"
                          />
                        </div>
                        <div className="p-6">
                          <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                            {dropdownItem.title}
                          </h3>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {dropdownItem.description}
                          </p>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="md:hidden fixed top-16 left-0 right-0 bg-white shadow-lg z-40 border-t"
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item) => (
                <div key={item.title}>
                  {item.hasDropdown ? (
                    <div className="space-y-2">
                      <div className="text-lg font-semibold text-gray-900 px-3 py-2">
                        {item.title}
                      </div>
                      <div className="pl-4 space-y-2">
                        {item.dropdownItems?.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.title}
                            to={dropdownItem.path}
                            onClick={closeMobileMenu}
                            className="block text-gray-600 hover:text-primary-600 px-3 py-2 text-sm transition-colors"
                          >
                            {dropdownItem.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.path || '/'}
                      onClick={closeMobileMenu}
                      className="block text-gray-700 hover:text-primary-600 px-3 py-2 text-lg font-medium transition-colors"
                    >
                      {item.title}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t space-y-3">
                <a
                  href="https://www.linkedin.com/company/er-hr-global/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center text-gray-500 hover:text-primary-600 transition-colors duration-200 px-3 py-2"
                  aria-label="Visit our LinkedIn page"
                >
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  Follow us on LinkedIn
                </a>
                <Link
                  to="/contact"
                  onClick={closeMobileMenu}
                  className="block bg-primary-600 text-white text-center px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDropdown}
            className="fixed inset-0 bg-black bg-opacity-50 z-30"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
